import { useEffect, useState } from "react";
import { CheckCheck } from "lucide-react";
import { Empty, PageHeader, PriorityBadge } from "@/components/common";
import { useFleet } from "@/context/FleetContext";
import { api } from "@/lib/api";
import { SOURCES, eventMeta, fmtShort, timeAgo } from "@/lib/fleet";
import { cn } from "@/lib/utils";

export default function Notifications() {
  const { loadUnread, liveEvents } = useFleet();
  const [source, setSource] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState([]);

  const load = () => api.get("/notifications", { params: { source: source || undefined, unread: unreadOnly, limit: 200 } }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [source, unreadOnly, liveEvents.length]);

  const read = async (n) => {
    if (n.read) return;
    await api.post(`/notifications/${n.id}/read`);
    setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    loadUnread();
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
    loadUnread();
  };

  return (
    <div>
      <PageHeader eyebrow="Notification Center" title="Notifikasi" subtitle="Notifikasi dari kendaraan, geofence, alarm dan sistem."
        actions={<button onClick={readAll} className="flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-bold hover:border-primary" data-testid="notifications-read-all"><CheckCheck className="h-4 w-4" /> Tandai semua dibaca</button>} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[["", "Semua"], ...Object.entries(SOURCES)].map(([k, l]) => (
          <button key={k} onClick={() => setSource(k)} data-testid={`notif-source-${k || "all"}`}
            className={cn("rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors", source === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{l}</button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs font-semibold">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="accent-yellow-500" data-testid="notif-unread-only" /> Belum dibaca saja
        </label>
      </div>
      <div className="gt-card divide-y divide-border/60 overflow-hidden" data-testid="notification-list">
        {items.map((n) => {
          const Icon = eventMeta(n.type).icon;
          return (
            <button key={n.id} onClick={() => read(n)} className={cn("flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-accent/50", !n.read && "bg-primary/[0.04]")} data-testid={`notification-item-${n.id}`}>
              <span className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-primary")} data-testid={`notification-unread-dot-${n.id}`} />
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent"><Icon className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className={cn("text-[13.5px]", n.read ? "font-medium" : "font-bold")}>{n.title}</span>
                  <PriorityBadge priority={n.priority} />
                  <span className="rounded-md bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{SOURCES[n.source] || n.source}</span>
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{n.vehicle_name ? <>{n.vehicle_name} · <span className="font-mono">{n.plate}</span> — </> : null}{n.message}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block font-mono text-[11px]">{fmtShort(n.created_at)}</span>
                <span className="block text-[10px] text-muted-foreground">{timeAgo(n.created_at)} · {n.read ? "Dibaca" : "Baru"}</span>
              </span>
            </button>
          );
        })}
        {!items.length && <Empty title="Tidak ada notifikasi" />}
      </div>
    </div>
  );
}
